/** ***********
*
* <websocket.service.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { Injectable } from '@angular/core';
import { Action, ActionCreator, Store } from '@ngrx/store';
import { combineLatest, Subscription, BehaviorSubject, OperatorFunction, Observable, merge, iif, of } from 'rxjs';
import { filter, map, take, switchMap, tap, delay, startWith } from 'rxjs/operators';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { environment } from 'src/environments/environment';
import { Asset } from '../models/asset';
import { Company } from '../models/company';
import { Family, ParentFamily } from '../models/family';
import { Site } from '../models/site';
import { Planning } from '../models/planning';
import { Certificate } from '../models/certificate';
import { successCreateAssets, successDeleteAssets, successUpdateAssets } from '../store/actions/asset.actions';
import { successCreateCompanies, successDeleteCompanies, successUpdateCompanies } from '../store/actions/company.actions';
import { successCreateFamilies, successDeleteFamilies, successUpdateFamilies } from '../store/actions/family.action';
import { successCreateParentFamilies, successDeleteParentFamilies, successUpdateParentFamilies } from '../store/actions/parent-family.action';
import { successCreateSites, successDeleteSites, successUpdateSites } from '../store/actions/site.action';
import { successCreateTrackers, successDeleteTrackers, successUpdateTrackers } from '../store/actions/tracker.action';
import { successCreatePlanning, successDeletePlanning, successEditPlanning } from '../store/actions/planning.action';
import { successCreateCertificateRequest, successDeleteCertificateRequest, successUpdateCertificateRequest } from '../store/actions/certificate.action';
import { selectAssetLoaded$ } from '../store/selectors/asset.selectors';
import { selectCompaniesLoaded$ } from '../store/selectors/company.selectors';
import { selectFamilyLoaded$ } from '../store/selectors/family.selectors';
import { selectParentFamilyLoaded$ } from '../store/selectors/parent-family.selectors';
import { selectSiteLoaded$ } from '../store/selectors/site.selectors';
import { selectTrackerLoaded$ } from '../store/selectors/tracker.selectors';
import { selectPlanningLoaded$, selectPlanning$ } from 'src/app/store/selectors/planning.selectors';
import { PlanningService } from './planning.service';
import moment from 'moment';
import { FilterService } from './filter.service';
import { SnackbarComponent } from '../components/shared/snackbar/snackbar';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, EventType, NavigationEnd, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { intersection, isEqual, pick } from 'lodash';
import { Banner, BannerService, WSBanner } from './banner.service';

export interface ConnectionPayload {
    connectionId: string;
}

enum MessageType {
    ASSET = 'asset',
    FAMILY = 'family1', // family type from db
    PARENT_FAMILY = 'family0', // parentFamily type form db
    DEVICE = 'tracker',
    CONNECT = 'connectionId',
    COMPANY = 'company',
    SITE = 'site',
    PLANNED = 'planned',
    OPERATION = 'operation',
    IMPLIED_OPERATION = 'implied_operation',
    CERTIFICATE = 'certificate',
    ANNOUNCEMENT = 'announcement',
    ASSET_DELETE = 'asset|delete',
    FAMILY_DELETE = 'family1|delete', // family type from db
    PARENT_FAMILY_DELETE = 'family0|delete',
    COMPANY_DELETE = 'company|delete',
    SITE_DELETE = 'site|delete',
}

enum MessageAction {
    CREATE = 'INSERT',
    UPDATE = 'MODIFY',
    DELETE = 'REMOVE',
    DEFAULT = 'default'
}

interface WebSocketMessage<T = Asset | Family | ParentFamily | Site | Company | Planning | ConnectionPayload | Certificate | WSBanner> {
    type: MessageType;
    action: MessageAction;
    message: T;
}

interface LastChange {
  type: MessageType;
  action: MessageAction;
  value: Action;
}

type MessagesTree = {
    [type in MessageType]?: {
        [action in MessageAction]?: any[];
    };
};

interface StoreAction<T extends string = string, R = any> {
    action: ActionCreator<T, (props: {
        [payload: string]: R;
    }) => {
        [payload: string]: R;
    } & Action<T>>,
    props?: (t: ReturnType<ActionCreator<T>>) => R,
}

@Injectable({
    providedIn: 'root',
})
export class WebSocketService {
    private readonly MAX_CONNEXION_TRIES = 10;
    private readonly BUFFER_DELAY = 10000;
    public readonly MAX_FILTERED_DATA_LENGTH = 3000;
    private readonly VIEWS_WITHOUT_REALTIME = [
        'rights/manage-requests',
        'rights/request',
        'rights/manage-user-roles',
        'alert',
        'kpi',
    ];
    private _connectionTries = 0;
    private _webSocket: WebSocketSubject<WebSocketMessage>;
    private _subscription = new Subscription();
    public isWebSocketConnected$ = new BehaviorSubject<boolean>(false);
    public webSocketRealTime$: Observable<boolean>;
    public bufferDelay$ = new BehaviorSubject<number>(30000);
    public isManualDisconnection = false;


    /**
     * Mapping of redux actions regarding the type of message
     */
    private _reduxActionsMapping: Partial<Record<
        MessageType,
        Partial<Record<MessageAction, StoreAction>>
    >> = {
        [MessageType.COMPANY]: {
            [MessageAction.CREATE]: { action: successCreateCompanies },
            [MessageAction.UPDATE]: { action: successUpdateCompanies },
            [MessageAction.DELETE]: { action: successDeleteCompanies }
        },
        [MessageType.COMPANY_DELETE]: {
            [MessageAction.DELETE]: { action: successDeleteCompanies }
        },
        [MessageType.SITE]: {
            [MessageAction.CREATE]: { action: successCreateSites },
            [MessageAction.UPDATE]: { action: successUpdateSites },
            [MessageAction.DELETE]: { action: successDeleteSites }
        },
        [MessageType.SITE_DELETE]: {
            [MessageAction.DELETE]: { action: successDeleteSites }
        },
        [MessageType.PARENT_FAMILY]: {
            [MessageAction.CREATE]: { action: successCreateParentFamilies, props: (item: ReturnType<typeof successCreateParentFamilies>) => item.family },
            [MessageAction.UPDATE]: { action: successUpdateParentFamilies },
            [MessageAction.DELETE]: { action: successDeleteParentFamilies }
        },
        [MessageType.PARENT_FAMILY_DELETE]: {
            [MessageAction.DELETE]: { action: successDeleteParentFamilies }
        },
        [MessageType.FAMILY]: {
            [MessageAction.CREATE]: { action: successCreateFamilies, props: (item: ReturnType<typeof successCreateFamilies>) => item.family },
            [MessageAction.UPDATE]: { action: successUpdateFamilies },
            [MessageAction.DELETE]: { action: successDeleteFamilies }
        },
        [MessageType.FAMILY_DELETE]: {
            [MessageAction.DELETE]: { action: successDeleteFamilies }
        },
        [MessageType.ASSET]: {
            [MessageAction.CREATE]: { action: successCreateAssets },
            [MessageAction.UPDATE]: { action: successUpdateAssets },
            [MessageAction.DELETE]: { action: successDeleteAssets }
        },
        [MessageType.ASSET_DELETE]: {
            [MessageAction.DELETE]: { action: successDeleteAssets }
        },
        [MessageType.DEVICE]: {
            [MessageAction.CREATE]: { action: successCreateTrackers },
            [MessageAction.UPDATE]: { action: successUpdateTrackers },
            [MessageAction.DELETE]: { action: successDeleteTrackers }
        },
        [MessageType.PLANNED]: {
            [MessageAction.CREATE]: { action: successCreatePlanning },
            [MessageAction.UPDATE]: { action: successEditPlanning },
            [MessageAction.DELETE]: { action: successDeletePlanning },
        },
        [MessageType.OPERATION]: {
            [MessageAction.CREATE]: { action: successCreatePlanning },
            [MessageAction.UPDATE]: { action: successEditPlanning },
            [MessageAction.DELETE]: { action: successDeletePlanning },
        },
        [MessageType.IMPLIED_OPERATION]: {
            [MessageAction.CREATE]: { action: successCreatePlanning },
            [MessageAction.UPDATE]: { action: successEditPlanning },
            [MessageAction.DELETE]: { action: successDeletePlanning },
        },
        [MessageType.CERTIFICATE]: {
            [MessageAction.CREATE]: { action: successCreateCertificateRequest },
            [MessageAction.UPDATE]: { action: successUpdateCertificateRequest },
            [MessageAction.DELETE]: { action: successDeleteCertificateRequest },
        }
    };
    // Declare here the selectors returning loaded property for each store managed by websockets
    private _storesLoaded = [
        this.store.select(selectCompaniesLoaded$),
        this.store.select(selectSiteLoaded$),
        this.store.select(selectParentFamilyLoaded$),
        this.store.select(selectFamilyLoaded$),
        this.store.select(selectAssetLoaded$),
        this.store.select(selectTrackerLoaded$),
        this._router.events.pipe(
            switchMap((event) => iif(
                () => {
                    switch (event.type) {
                        case EventType.NavigationSkipped:
                            return event.url.includes('planning');
                        case EventType.Scroll:
                            return event.routerEvent.url.includes('planning');
                        case EventType.NavigationEnd:
                            return event.url.includes('planning');
                    }
                    return false;
                },
                this.store.select(selectPlanningLoaded$),
                of(true),
            )),
        ),
    ];
    private _lastChanges$: Observable<LastChange>;

    constructor(
        private filterService: FilterService,
        private store: Store,
        private _planningService: PlanningService,
        private _snackBar: SnackbarComponent,
        private _translate: TranslateService,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _actions$: Actions,
        private readonly _bannerService: BannerService,
    ) {
        // websocket real time observable construction

        /**
         * Observable that says if the realtime is implemented for the current view
         */
        const isWebsocketActiveForCurrentView$ = new BehaviorSubject(true);

        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map((event: NavigationEnd) => event.url),
            map(url => !this.VIEWS_WITHOUT_REALTIME.some((view) => url.includes(view))),
        ).subscribe((value) => isWebsocketActiveForCurrentView$.next(value));

        this.webSocketRealTime$ = combineLatest([
            this.filterService.filteredDataLength$,
            this.isWebSocketConnected$,
            isWebsocketActiveForCurrentView$
        ]).pipe(
            map(([filteredDataLength, isWebSocketConnected, isWebsocketActiveForCurrentView]) => {
                return isWebsocketActiveForCurrentView && isWebSocketConnected && filteredDataLength < this.MAX_FILTERED_DATA_LENGTH ? true : false;
            })
        );

        this._listenToStoreChanges();
    }

    private _listenToStoreChanges() {
      const actionsByMessageType = Object.entries(this._reduxActionsMapping);
      const listenedActions: Observable<{
        type: MessageType,
        action: MessageAction,
        value: Action,
      }>[] = [];
      actionsByMessageType.forEach(
        ([ messageType, typeActions ]) => {
          Object.entries(typeActions).forEach(
            ([ messageAction, { action, props } ]) => {
                props ??= (data: any) => data.payload[0];
              listenedActions.push(
                this._actions$.pipe(
                  ofType(action),
                  map((value: any) => ({
                    type: messageType as MessageType,
                    action: messageAction as MessageAction,
                    value: props(value),
                  }))
                )
              );
            }
          );
        }
      );
      this._lastChanges$ = merge(...listenedActions);
    }


    // Create New Websocket
    private getNewWebSocket() {
        this._connectionTries++;
        const token = localStorage.getItem('token');
        return webSocket<WebSocketMessage>({
            url: `${environment.WS_ENDPOINT}?token=${token}`,
            openObserver: {
                next: () => {
                    this.isWebSocketConnected$.next(true);
                    // reset connection tries
                    this._connectionTries = 0;
                }
            },
            closeObserver: {
                next: () => {
                    this.isWebSocketConnected$.next(false);
                    this._webSocket = undefined;
                    if (!this.isManualDisconnection) {
                        // automatic reconnection when it is closed
                        if (this._connectionTries <= this.MAX_CONNEXION_TRIES && !this.isManualDisconnection) {
                            setTimeout(() => this.connect(), 1000);
                        } else {
                            this._snackBar.open(this._translate.instant('REAL_TIME_ERROR'), 'red-snackbar');
                        }
                    } else {
                        // reset the boolean
                        this.isManualDisconnection = false;
                    }
                }
            },
        });
    }

    public connect() {
        // Initialize Websockets if not available
        if (!this._webSocket || this._webSocket.closed) {
            this._webSocket = this.getNewWebSocket();
        }

        // Handle the WebSockets messages when all the store their update are loaded
        combineLatest(this._storesLoaded).pipe(
            filter(stores => stores.every(Boolean)),
            take(1),
            switchMap(() =>
                this._webSocket.pipe(
                    map((message: WebSocketMessage) => {
                        if (!message.action) {
                            message.action = MessageAction.DEFAULT;
                        }
                        return message;
                    }),
                    filter(message => {
                        if (message.type === MessageType.ANNOUNCEMENT) {
                            this._handleAnnouncement(message as WebSocketMessage<WSBanner>);
                            return false;
                        }
                        return true;
                    }),
                    this._bufferWhileBusy,
                    map(messages => messages.filter((message) => this._filterMessages(message)))
                )
            ))
            .subscribe((messages: WebSocketMessage[]) => {
                // Check wheather internet disconnection or get any value
                if (!window.navigator.onLine) {
                    this.disconnect();
                }
                this._handleBuffer(messages);
            });
    }

    /**
     * Operator function to handle the bufferArray automatically based on :
     * - the filter busy state
     * - the number of items filtered
     * - a delay duration
     */
    private _bufferWhileBusy: OperatorFunction<WebSocketMessage, WebSocketMessage[]> = (observerInput: Observable<WebSocketMessage>) => {

        const bufferArray = [];
        const lastChanges: LastChange[] = [];
        let canContinue = true;

        return combineLatest([
            this.filterService.workerBusy$,
            this.webSocketRealTime$,
            this._lastChanges$.pipe(
                startWith(null),
                tap(change => {
                    if (change) {
                        lastChanges.push(change);
                    }
                }),
            ),
            observerInput.pipe( // fill the buffer
              tap((message) => {
                // Only add the message if it has not already been triggered by an action before locally
                if (!lastChanges.find(
                  change => {
                    if (message.type === change.type && message.action === change.action) {
                      // Only compare the common property as the action payload can have more properties than the websocket payload
                      const commonProperties = intersection(Object.keys(message.message), Object.keys(change.value));
                      return isEqual(
                        pick(message.message, commonProperties),
                        pick(change.value, commonProperties)
                      );
                    }
                    return false;
                  }
                )) {
                  // Add as it is not in the last changes
                  bufferArray.push(message);
                }
              })
            ),
        ]).pipe(
            filter(([isWorkerBusy, isRealTime]) => !isWorkerBusy && isRealTime && canContinue),
            map(() => bufferArray.splice(0, bufferArray.length)), // empty the buffer
            tap(() => canContinue = false),
            delay(this.BUFFER_DELAY), // a few second break for the laptop before continuing
            tap(() => {
              canContinue = true;
              lastChanges.splice(0, lastChanges.length);
            }),
        );
    }

    /**
     * check if planning message are in same range of the filter date planning
     */
    private _filterPlanningDate(message): boolean {
        if (message.type === MessageType.PLANNED) {
            if (!this._isDateBetween(message.message.confirmedDate, this._planningService.fromDate, this._planningService.toDate, message.message.plannedDate)) { return false; }
        }
        if (message.type === MessageType.OPERATION || message.type === MessageType.IMPLIED_OPERATION) {
            if (!this._isDateBetween(message.message.confirmedDate, this._planningService.fromDate, this._planningService.toDate)) { return false; }
        }
        return true;
    }

    private _filterMessages(message: WebSocketMessage): boolean {
        return [this._filterPlanningDate(message), this._filterDuplicate(message)].every(Boolean);
    }

    /**
     * Check if the event already exist in the planning
     */
    private _filterDuplicate(message: WebSocketMessage): boolean {
        let bool = true;
        if (message.action === MessageAction.CREATE && [MessageType.OPERATION, MessageType.PLANNED].includes(message.type)) {
            this.store.select(selectPlanning$).pipe(take(1)).subscribe((planning) => {
                for (const operation of planning) {
                    if ((message.message as Planning).operationId === operation.operationId) {
                        bool = false;
                        break;
                    }
                }
            });
        }
        return bool;
    }

    /**
     * plannedDate is optional because it's a specific case for Planned type we check two date
     * @param confirmedDate confirmedDate planning
     * @param fromDate fromDate filter planning
     * @param toDate toDate filter planning
     * @param plannedDate plannedDate of a Planned type Planning
     * @returns is date between two date filter
     */
    private _isDateBetween(confirmedDate: string, fromDate: string, toDate: string, plannedDate?: string): boolean {
        return plannedDate ? moment(plannedDate).isBetween(fromDate, toDate) || moment(confirmedDate).isBetween(fromDate, toDate) :
            moment(confirmedDate).isBetween(fromDate, toDate);
    }

    /**
     * Disconnect the websocket
     */
    public disconnect() {
        this._webSocket.complete();
        this._webSocket.unsubscribe();
        this._subscription.unsubscribe();
        this.isWebSocketConnected$.next(false);
    }

    /**
     * Bufferize the messages
     * @param messages Message coming from websocket
     */
    private _handleBuffer(messages: WebSocketMessage[]) {
        if (messages.length > 0) {
            // create a tree with messages sorted by type and action
            const messagesTree = this._getMessagesTree(messages);
            this._handleMessages(messagesTree);
        }
    }

    /**
     * Build tree of websocket coming messages
     * @param messagesTree Tree of messages
     */
    private _handleMessages(messagesTree: MessagesTree) {
        const messageTypes = Object.keys(messagesTree) as MessageType[];
        messageTypes.forEach(messageType => {
            const messageActions = Object.keys(messagesTree[messageType]) as MessageAction[];
            messageActions.forEach(messageAction => {
                const action = this._getStoreAction(messageType, messageAction);
                const payload = messagesTree[messageType][messageAction].map(__message => __message.message);
                setTimeout(() => {
                    if (action) {
                        this.store.dispatch(action({ payload: payload }));
                    }
                }, 0);
            });
        });
    }

    /**
     * Parse the message and format them as a tree
     * @param messages Array of websocket message
     * @return The message tree
     */
    private _getMessagesTree(messages: WebSocketMessage[]): MessagesTree {
        // sort messages by type
        messages.sort((a, b) => a.type > b.type ? 1 : -1);

        // Segregate messages by type then by action :
        let type: MessageType;
        // const messagesTree: { [key in MessageType]?: WebSocketMessage[] } = {};
        const messagesTree: MessagesTree = {};
        messages.forEach(message => {
            // if a new type of message is identified
            if (message.type !== type) {
                type = message.type;
                messagesTree[type] = {};
                // keep only the same type of messages
                const messagesByType = messages.filter(_message => _message.type === message.type);
                // sort by action
                messagesByType.sort((a, b) => a.action > b.action ? 1 : -1);
                // segregate messages by action
                let action: MessageAction;
                messagesByType.forEach((_message) => {
                    // if a new action is identified
                    // _message.action = undefined ? MessageAction.DEFAULT : _message.action;
                    if (_message.action !== action) {
                        action = _message.action;
                        const messagesByAction = messagesByType.filter(__message => __message.action === _message.action);
                        messagesTree[type][action] = messagesByAction;
                    }
                });
            }
        });
        return messagesTree;
    }

    /**
     * Get the correct action depending on params
     * @param type The type of object related to the action (asset, families...)
     * @param action The CRUD type of the action (create / update / delete / default)
     * @return The action
     */
    private _getStoreAction(type: MessageType, action: MessageAction): StoreAction['action'] {
        return this._reduxActionsMapping[type]?.[action]?.action;
    }

    /**
     * Display the annoucement
     * Does not rely on the store
     */
    private _handleAnnouncement({ message }: WebSocketMessage<WSBanner>) {
        const payload: Banner = {
            id: message.id,
            message: message.message,
            type: message.type,
            action: message.link ? {
                text: message.link,
                callback: () => window.open(message.link, '_blank'),
            } : null,
        };
        this._bannerService.addBanner(payload);
    }
}
